import type { ComponentProps } from "react"
import Separator from "@/v2-ui/separator"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import { TooltipLink } from "@/v2-backoffice-client/components/link"
import TenantUpdateButton from "@/v2-backoffice-client/tenant/update/TenantUpdateButton"
import TenantRemoveButton from "@/v2-backoffice-client/tenant/remove/TenantRemoveButton"
import TenantRestoreButton from "@/v2-backoffice-client/tenant/restore/TenantRestoreButton"
import TenantCustomerAddSelector from "@/v2-backoffice-client/tenant/customer/add/TenantCustomerAddSelector"
import TenantMultiSessionMenu from "@/v2-backoffice-client/tenant/multiSession/TenantMultiSessionMenu"
import TenantMigrateButton from "@/v2-backoffice-client/tenant/migration/TenantMigrateButton"
import { buildMixpanelBoardUrl } from "@/v2-backoffice-client/analytics/analytics.utils"
import {
  FENERUM_ACCOUNT_BASE_URL,
  HUBSPOT_BASE_URL,
} from "@/v2-backoffice-client/constants"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"

export type TenantViewToolbarProps = ComponentProps<"div"> & {
  tenant: TenantFragment | null | undefined
}

function TenantViewToolbar(props: TenantViewToolbarProps) {
  const { tenant, ...viewSheetToolbarProps } = props
  if (!tenant) return null

  return (
    <ViewSheet.Toolbar {...viewSheetToolbarProps}>
      <TooltipLink
        variant="ghost"
        isStacked
        iconSrc="hubspot"
        content="Open in HubSpot"
        disabled={!tenant.ExternalCustomerId}
        to={
          tenant.ExternalCustomerId
            ? `${HUBSPOT_BASE_URL}/${tenant.ExternalCustomerId}`
            : undefined
        }
      >
        HubSpot
      </TooltipLink>

      <TooltipLink
        variant="ghost"
        isStacked
        iconSrc="bank"
        content={
          tenant.BillingAccountUuid
            ? "Open the customer's billing account in Fenerum"
            : "The owning customer has no linked Fenerum billing account"
        }
        disabled={!tenant.BillingAccountUuid}
        to={
          tenant.BillingAccountUuid
            ? `${FENERUM_ACCOUNT_BASE_URL}/${tenant.BillingAccountUuid}/`
            : undefined
        }
      >
        Fenerum
      </TooltipLink>

      <TooltipLink
        variant="ghost"
        isStacked
        iconSrc="mixpanel"
        content="Open in Mixpanel"
        to={buildMixpanelBoardUrl({
          resourceType: "user",
          propertyName: "User.ActiveTenantId",
          propertyType: "number",
          filterOperator: "equals",
          filterValue: String(tenant.Id),
        })}
      >
        Mixpanel
      </TooltipLink>

      <Separator orientation="vertical" />

      <TenantUpdateButton tenant={tenant} isStacked>
        Update
      </TenantUpdateButton>

      <TenantCustomerAddSelector
        tenantId={tenant.Id}
        externalCustomerId={tenant.ExternalCustomerId}
        iconSrc="users-alt"
        variant="ghost"
        isStacked
        content="Link this team to a customer, moving its seats onto that customer's license."
      >
        Customer
      </TenantCustomerAddSelector>

      <TenantMultiSessionMenu tenantId={tenant.Id} />

      {!tenant.IsDeleted && (
        <TenantMigrateButton
          tenantId={tenant.Id}
          tenantName={tenant.Name}
          customerName={tenant.CustomerName}
          isStacked
        >
          Fusion Login
        </TenantMigrateButton>
      )}

      <Separator orientation="vertical" />

      {tenant.IsDeleted ? (
        <TenantRestoreButton tenantId={tenant.Id} isStacked>
          Restore
        </TenantRestoreButton>
      ) : (
        <TenantRemoveButton tenantId={tenant.Id} isStacked>
          Remove
        </TenantRemoveButton>
      )}
    </ViewSheet.Toolbar>
  )
}

export default TenantViewToolbar
